(() => {
  if (customElements.get('croma-facets')) {
    return;
  }

  class CromaFacets extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-facets-dialog]');
      this.lastTrigger = null;
      this.requestController = null;
      this.gridDensity = this.getInitialGridDensity();

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      this.dialog?.addEventListener('close', this.onDialogClose);
      window.addEventListener('popstate', this.onPopState);
      this.applyGridDensity(this.gridDensity);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('submit', this.onSubmit);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      window.removeEventListener('popstate', this.onPopState);
      this.requestController?.abort();
      document.body.classList.remove('croma-facets-open');
    }

    onChange = (event) => {
      const form = event.target.closest('[data-facets-form="desktop"]');

      if (!form) {
        return;
      }

      this.navigate(this.buildFormUrl(form), {
        focusTarget: event.target,
        preserveOpenGroups: true
      });
    };

    onSubmit = (event) => {
      const form = event.target.closest('[data-facets-form]');

      if (!form) {
        return;
      }

      event.preventDefault();
      const isMobile = form.dataset.facetsForm === 'mobile';

      this.navigate(this.buildFormUrl(form), {
        closeDialog: isMobile,
        preserveOpenGroups: !isMobile
      });
    };

    onClick = (event) => {
      const densityButton = event.target.closest('[data-grid-density]');

      if (densityButton) {
        this.applyGridDensity(densityButton.dataset.gridDensity, true);
        return;
      }

      const openButton = event.target.closest('[data-facets-open]');

      if (openButton) {
        this.openDialog(openButton);
        return;
      }

      if (event.target.closest('[data-facets-close]')) {
        this.closeDialog();
        return;
      }

      if (event.target === this.dialog) {
        this.closeDialog();
        return;
      }

      const link = event.target.closest('[data-facet-remove], .croma-pagination a');

      if (!link || !this.isPlainClick(event)) {
        return;
      }

      event.preventDefault();
      const isPagination = Boolean(link.closest('.croma-pagination'));

      this.navigate(new URL(link.href, window.location.origin), {
        closeDialog: Boolean(link.closest('[data-facets-dialog]')),
        scrollToResults: isPagination
      });
    };

    onDialogClose = () => {
      document.body.classList.remove('croma-facets-open');
      this.updateDialogTriggers(false);
      this.lastTrigger?.focus({ preventScroll: true });
    };

    onPopState = () => {
      this.navigate(new URL(window.location.href), {
        updateHistory: false
      });
    };

    openDialog(trigger) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') {
        return;
      }

      this.lastTrigger = trigger;
      document.body.classList.add('croma-facets-open');
      this.updateDialogTriggers(true);

      if (!this.dialog.open) {
        this.dialog.showModal();
      }
    }

    closeDialog(restoreFocus = true) {
      if (!this.dialog?.open) {
        return;
      }

      if (!restoreFocus) {
        this.lastTrigger = null;
      }

      this.dialog.close();
    }

    buildFormUrl(form) {
      const url = new URL(form.action || window.location.href, window.location.origin);
      const params = new URLSearchParams();

      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') {
          params.append(key, value);
        }
      });

      url.search = params.toString();
      return url;
    }

    async navigate(url, options = {}) {
      const targetUrl = new URL(url, window.location.origin);
      const openGroups = options.preserveOpenGroups ? this.getOpenGroups() : [];
      const focusIdentity = this.getFocusIdentity(options.focusTarget);

      this.requestController?.abort();
      this.requestController = new AbortController();
      const activeController = this.requestController;
      const requestUrl = new URL(targetUrl);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      this.setLoading(true);

      try {
        const response = await fetch(requestUrl, {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: activeController.signal
        });

        if (!response.ok) {
          throw new Error(`Collection request failed with status ${response.status}`);
        }

        const responseText = await response.text();
        const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const incomingSection = parsedDocument.getElementById(`CromaCollectionSection-${this.dataset.sectionId}`);

        if (!incomingSection) {
          throw new Error('Collection section was not found in the response');
        }

        if (options.closeDialog) {
          this.closeDialog(false);
        }

        this.replaceFragment(incomingSection, '[data-facets-toolbar]');
        this.replaceFragment(incomingSection, '[data-active-facets]');
        this.replaceFragment(incomingSection, '[data-facets-results]');
        this.replaceFragment(incomingSection, '[data-facets-dialog-fields]');
        this.replaceFragment(incomingSection, '[data-facets-dialog-footer]');
        this.applyGridDensity(this.gridDensity);

        if (options.updateHistory !== false) {
          window.history.pushState({ cromaFacets: true }, '', `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`);
        }

        this.restoreOpenGroups(openGroups);
        this.restoreFocus(focusIdentity);

        if (options.closeDialog) {
          this.querySelector('[data-facets-open]')?.focus({ preventScroll: true });
        }

        this.announceResults();

        if (options.scrollToResults) {
          this.querySelector('[data-facets-results]')?.scrollIntoView({ block: 'start' });
        }

        document.dispatchEvent(new CustomEvent('croma:collection:updated', {
          detail: {
            sectionId: this.dataset.sectionId,
            url: targetUrl.toString()
          }
        }));
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.error('Unable to update collection', error);
        window.location.assign(targetUrl);
      } finally {
        if (this.requestController === activeController) {
          this.requestController = null;
          this.setLoading(false);
        }
      }
    }

    replaceFragment(incomingSection, selector) {
      const currentFragment = this.querySelector(selector);
      const incomingFragment = incomingSection.querySelector(selector);

      if (currentFragment && incomingFragment) {
        currentFragment.innerHTML = incomingFragment.innerHTML;
      }
    }

    getOpenGroups() {
      return Array.from(this.querySelectorAll('[data-facets-form="desktop"] [data-filter-key][open]'))
        .map((group) => group.dataset.filterKey);
    }

    restoreOpenGroups(keys) {
      if (!keys.length) {
        return;
      }

      this.querySelectorAll('[data-facets-form="desktop"] [data-filter-key]').forEach((group) => {
        if (keys.includes(group.dataset.filterKey)) {
          group.open = true;
        }
      });
    }

    getFocusIdentity(target) {
      if (!target?.name) {
        return null;
      }

      return {
        name: target.name,
        type: target.type,
        value: target.value
      };
    }

    restoreFocus(identity) {
      if (!identity) {
        return;
      }

      const controls = Array.from(this.querySelectorAll('[data-facets-form="desktop"] [name]'));
      const target = controls.find((control) => (
        control.name === identity.name &&
        (identity.type !== 'checkbox' || control.value === identity.value)
      ));

      target?.focus({ preventScroll: true });
    }

    announceResults() {
      const count = this.querySelector('[data-facets-count]')?.textContent.trim();
      const status = this.querySelector('[data-facets-status]');

      if (count && status) {
        status.textContent = count;
      }
    }

    getInitialGridDensity() {
      const defaultDensity = this.dataset.defaultGridDensity === 'compact' ? 'compact' : 'comfortable';

      if (this.dataset.gridDensityEnabled !== 'true' || window.Shopify?.designMode) {
        return defaultDensity;
      }

      try {
        const savedDensity = window.localStorage.getItem('croma:collection-grid-density');
        return savedDensity === 'compact' || savedDensity === 'comfortable' ? savedDensity : defaultDensity;
      } catch (error) {
        return defaultDensity;
      }
    }

    applyGridDensity(density, persist = false) {
      if (this.dataset.gridDensityEnabled !== 'true') {
        return;
      }

      this.gridDensity = density === 'compact' ? 'compact' : 'comfortable';
      this.classList.toggle('is-grid-compact', this.gridDensity === 'compact');

      this.querySelectorAll('[data-grid-density]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.gridDensity === this.gridDensity));
      });

      if (persist && !window.Shopify?.designMode) {
        try {
          window.localStorage.setItem('croma:collection-grid-density', this.gridDensity);
        } catch (error) {
          // The control still works when storage is unavailable.
        }
      }
    }

    updateDialogTriggers(isExpanded) {
      this.querySelectorAll('[data-facets-open]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(isExpanded));
      });
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.querySelector('[data-facets-results]')?.setAttribute('aria-busy', String(isLoading));

      if (this.dialog) {
        this.dialog.setAttribute('aria-busy', String(isLoading));
      }
    }

    isPlainClick(event) {
      return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    }
  }

  customElements.define('croma-facets', CromaFacets);
})();
